/**
 * PTEL Tool - Auth Tokens
 * Design Tokens para componentes de autenticación
 * Alineados con tokens.css y Step2/tokens.ts
 * 
 * @module components/auth/auth.tokens
 * @version 1.1.0
 * @see src/styles/tokens.css
 */

import type { Theme } from './auth.types';

// ============================================================================
// TOKEN TYPES
// ============================================================================

export interface AuthTokenSet {
  // Backgrounds
  bg: string;
  bgCard: string;
  bgInput: string;
  bgInputHover: string;
  
  // Borders
  border: string;
  borderHover: string;
  borderFocus: string;
  borderError: string;
  
  // Text
  text: string;
  textSecondary: string;
  textMuted: string;
  textPlaceholder: string;
  
  // Primary action (teal)
  primary: string;
  primaryHover: string;
  primaryText: string;
  
  // States
  error: string;
  errorBg: string;
  success: string;
  successBg: string;
  
  // Password strength
  strengthWeak: string;
  strengthFair: string;
  strengthGood: string;
  strengthStrong: string;
  
  // Focus ring
  focusRing: string;
  focusRingOffset: string;
  
  // Modal
  backdrop: string;
}

export type AuthThemeTokens = Record<Theme, AuthTokenSet>;

// ============================================================================
// AUTH TOKENS
// ============================================================================

export const authTokens: AuthThemeTokens = {
  dark: {
    bg: '#1a1a18',
    bgCard: '#262624',
    bgInput: '#2d2d2b',
    bgInputHover: '#353533',
    
    border: '#4a4a48',
    borderHover: '#5a5a58',
    borderFocus: '#2dd4bf',
    borderError: '#ef4444',
    
    text: '#fafaf9',
    textSecondary: '#a8a8a4',
    textMuted: '#737370',
    textPlaceholder: '#5a5a55',
    
    primary: '#2dd4bf',
    primaryHover: '#5eead4',
    primaryText: '#1a1a18',
    
    error: '#ef4444',
    errorBg: 'rgba(239, 68, 68, 0.15)',
    success: '#2dd4bf',
    successBg: 'rgba(45, 212, 191, 0.15)',
    
    strengthWeak: '#ef4444',
    strengthFair: '#f97316',
    strengthGood: '#3b82f6',
    strengthStrong: '#2dd4bf',
    
    focusRing: 'rgba(45, 212, 191, 0.5)',
    focusRingOffset: '#262624',
    
    backdrop: 'rgba(0, 0, 0, 0.7)',
  },
  light: {
    bg: '#FFFFFF',
    bgCard: '#F5F5F4',
    bgInput: '#FFFFFF',
    bgInputHover: '#FAFAFA',
    
    border: '#D4D4D3',
    borderHover: '#A8A8A4',
    borderFocus: '#0D9488',
    borderError: '#DC2626',
    
    text: '#171717',
    textSecondary: '#525252',
    textMuted: '#A3A3A3',
    textPlaceholder: '#A3A3A3',
    
    primary: '#0D9488',
    primaryHover: '#0F766E',
    primaryText: '#FFFFFF',
    
    error: '#DC2626',
    errorBg: 'rgba(220, 38, 38, 0.1)',
    success: '#0D9488',
    successBg: 'rgba(13, 148, 136, 0.1)',
    
    strengthWeak: '#DC2626',
    strengthFair: '#EA580C',
    strengthGood: '#2563EB',
    strengthStrong: '#0D9488',
    
    focusRing: 'rgba(13, 148, 136, 0.4)',
    focusRingOffset: '#F5F5F4',
    
    backdrop: 'rgba(0, 0, 0, 0.5)',
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getAuthTokens(theme: Theme): AuthTokenSet {
  return authTokens[theme];
}

export function getStrengthColor(score: number, theme: Theme): string {
  const t = authTokens[theme];
  if (score >= 4) return t.strengthStrong;
  if (score >= 3) return t.strengthGood;
  if (score >= 2) return t.strengthFair;
  return t.strengthWeak;
}

export function getStrengthLabel(score: number): string {
  if (score >= 4) return 'Muy fuerte';
  if (score >= 3) return 'Fuerte';
  if (score >= 2) return 'Regular';
  if (score >= 1) return 'Débil';
  return 'Muy débil';
}

export function calculatePasswordStrength(password: string): number {
  if (!password) return 0;
  
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  
  return Math.min(score, 4);
}

// ============================================================================
// COMMON STYLES
// ============================================================================

export function getInputStyles(theme: Theme, hasError: boolean = false) {
  const t = authTokens[theme];
  
  return {
    container: { position: 'relative' as const },
    input: {
      width: '100%',
      padding: '10px 12px 10px 40px',
      background: t.bgInput,
      border: `1px solid ${hasError ? t.borderError : t.border}`,
      borderRadius: '8px',
      color: t.text,
      fontSize: '14px',
      fontFamily: "'JetBrains Mono', monospace",
      outline: 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    },
    inputFocus: {
      borderColor: hasError ? t.borderError : t.borderFocus,
      boxShadow: `0 0 0 3px ${hasError ? t.errorBg : t.focusRing}`,
    },
    icon: {
      position: 'absolute' as const,
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: t.textMuted,
      pointerEvents: 'none' as const,
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '14px',
      fontWeight: 500,
      color: t.textSecondary,
      fontFamily: "'Space Grotesk', sans-serif",
    },
    error: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginTop: '4px',
      fontSize: '12px',
      color: t.error,
    },
  };
}

export function getButtonStyles(theme: Theme, variant: 'primary' | 'secondary' = 'primary') {
  const t = authTokens[theme];
  
  if (variant === 'primary') {
    return {
      base: {
        width: '100%',
        padding: '10px 16px',
        background: t.primary,
        color: t.primaryText,
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: 600,
        fontFamily: "'Space Grotesk', sans-serif",
        cursor: 'pointer',
        transition: 'background 0.15s, transform 0.1s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      },
      hover: { background: t.primaryHover },
      disabled: { opacity: 0.5, cursor: 'not-allowed' },
      focus: {
        outline: 'none',
        boxShadow: `0 0 0 2px ${t.focusRingOffset}, 0 0 0 4px ${t.focusRing}`,
      },
    };
  }
  
  return {
    base: {
      width: '100%',
      padding: '10px 16px',
      background: 'transparent',
      color: t.textSecondary,
      border: `1px solid ${t.border}`,
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: 500,
      fontFamily: "'Space Grotesk', sans-serif",
      cursor: 'pointer',
      transition: 'border-color 0.15s, color 0.15s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    hover: { borderColor: t.borderHover, color: t.text },
    disabled: { opacity: 0.5, cursor: 'not-allowed' },
    focus: {
      outline: 'none',
      boxShadow: `0 0 0 2px ${t.focusRingOffset}, 0 0 0 4px ${t.focusRing}`,
    },
  };
}

export function getCardStyles(theme: Theme) {
  const t = authTokens[theme];
  
  return {
    container: {
      background: t.bgCard,
      border: `1px solid ${t.border}`,
      borderRadius: '12px',
      padding: '24px',
      boxShadow: theme === 'dark' 
        ? '0 4px 24px rgba(0, 0, 0, 0.4)'
        : '0 4px 24px rgba(0, 0, 0, 0.08)',
    },
    title: {
      fontSize: '24px',
      fontWeight: 700,
      color: t.text,
      fontFamily: "'Space Grotesk', sans-serif",
      textAlign: 'center' as const,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: t.textSecondary,
      textAlign: 'center' as const,
      marginBottom: '24px',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      margin: '24px 0',
      gap: '12px',
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      background: t.border,
    },
    dividerText: {
      fontSize: '14px',
      color: t.textMuted,
    },
    link: {
      color: t.primary,
      fontWeight: 500,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      fontSize: 'inherit',
      fontFamily: 'inherit',
    },
    footer: {
      textAlign: 'center' as const,
      fontSize: '14px',
      color: t.textSecondary,
    },
  };
}

export function getAlertStyles(theme: Theme, variant: 'error' | 'success' = 'error') {
  const t = authTokens[theme];
  const isError = variant === 'error';
  
  return {
    container: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px',
      background: isError ? t.errorBg : t.successBg,
      border: `1px solid ${isError ? t.error : t.success}30`,
      borderRadius: '8px',
      marginBottom: '16px',
    },
    icon: {
      flexShrink: 0,
      marginTop: '1px',
      color: isError ? t.error : t.success,
    },
    text: {
      fontSize: '14px',
      color: isError ? t.error : t.success,
      lineHeight: 1.5,
    },
  };
}
